import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { API_ENDPOINTS } from '../config/api'
import { downloadExport } from '../utils/downloadExport'
import { Card, Badge, PageLayout, PageHeader } from '../components/ui'
import ResultVisualization from '../components/results/ResultVisualization'
import type { GroupResult } from '../types'

interface SurveyResponse {
  id: string
  respondent_email?: string
  responses: Record<string, any>
  score?: number
  score_breakdown?: Record<string, number>
  group_results?: GroupResult[]
  overall_percentage?: number
  submitted_at: string
}

interface Question {
  id: string
  text: string
  type: string
  order: number
}

interface Survey {
  id: string
  title: string
  description?: string
  result_type?: string
  questions?: Question[]
}

const SurveyResponsesPage: React.FC = () => {
  const intl = useIntl()
  const { surveyId = '' } = useParams<{ surveyId: string }>()
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('org')
  useAuth()
  const { locale } = useLanguage()
  const [survey, setSurvey] = useState<Survey | null>(null)
  const [responses, setResponses] = useState<SurveyResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(false)

  const handleExportCsv = async () => {
    setExporting(true)
    setExportError(false)
    try {
      await downloadExport(
        API_ENDPOINTS.surveys.responsesExportCsv(surveyId, locale),
        `${survey?.title || 'responses'}.csv`,
      )
    } catch (err) {
      console.error('Export failed:', err)
      setExportError(true)
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const headers = { 'Authorization': token ? `Bearer ${token}` : '' }

        const [surveyRes, responsesRes] = await Promise.all([
          fetch(API_ENDPOINTS.surveys.byId(surveyId), { headers }),
          fetch(API_ENDPOINTS.surveys.responses(surveyId), { headers })
        ])

        if (surveyRes.ok) setSurvey(await surveyRes.json())
        if (responsesRes.ok) setResponses(await responsesRes.json())
      } catch (err) {
        console.error('Failed to fetch responses:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [surveyId])

  const getQuestionText = (questionId: string) => {
    return survey?.questions?.find(q => q.id === questionId)?.text || questionId
  }

  const formatAnswer = (value: any) => {
    if (value === true) return intl.formatMessage({ id: 'responses.yes' })
    if (value === false) return intl.formatMessage({ id: 'responses.no' })
    if (typeof value === 'number') return String(value)
    if (Array.isArray(value)) return value.join(', ')
    if (typeof value === 'object' && value !== null) {
      if (value.country) {
        return value.region ? `${value.country}, ${value.region}` : value.country
      }
      return JSON.stringify(value)
    }
    return String(value || '-')
  }

  return (
    <PageLayout maxWidth="xl">
      <PageHeader
        title={intl.formatMessage(
          { id: 'responses.pageTitle' },
          { title: survey?.title || intl.formatMessage({ id: 'responses.defaultSurveyTitle' }) }
        )}
        subtitle={survey?.description || undefined}
        backTo={orgId ? `/organizations/${orgId}?tab=surveys` : `/surveys/${surveyId}/share`}
        backLabel={orgId
          ? intl.formatMessage({ id: 'responses.backToOrganization' })
          : intl.formatMessage({ id: 'responses.backToShare' })}
      />

      {loading ? (
        <Card className="text-center text-foreground-muted"><FormattedMessage id="responses.loading" /></Card>
      ) : responses.length === 0 ? (
        <Card className="text-center">
          <p className="text-foreground-muted"><FormattedMessage id="responses.empty" /></p>
          <p className="mt-1 text-sm text-foreground-muted"><FormattedMessage id="responses.emptyHint" /></p>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card padding="sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-foreground-muted">
                <FormattedMessage id="responses.count" values={{ count: responses.length }} />
              </p>
              <button
                className="px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-background disabled:opacity-50"
                disabled={exporting}
                onClick={handleExportCsv}
              >
                {exporting
                  ? intl.formatMessage({ id: 'common.exporting' })
                  : intl.formatMessage({ id: 'common.exportCsv' })}
              </button>
            </div>
            {exportError && (
              <p className="mt-2 text-sm text-error"><FormattedMessage id="common.exportFailed" /></p>
            )}
          </Card>

          {responses.map((resp, index) => (
            <Card key={resp.id} padding="none" className="overflow-hidden">
              <div className="px-4 py-4 sm:px-6 bg-background-alt border-b border-border flex justify-between items-center">
                <div>
                  <span className="text-sm font-medium text-foreground">
                    <FormattedMessage id="responses.responseNumber" values={{ number: index + 1 }} />
                  </span>
                  {resp.respondent_email && (
                    <span className="ml-3 text-sm text-foreground-muted">{resp.respondent_email}</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {resp.score != null && (
                    <Badge variant="primary">
                      <FormattedMessage id="responses.score" values={{ score: resp.score }} />
                    </Badge>
                  )}
                  <span className="text-xs text-foreground-muted">
                    {new Date(resp.submitted_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="px-4 py-4 sm:px-6">
                {/* Group result visualization (for structured surveys) */}
                {resp.group_results && resp.group_results.length > 0 && (
                  <div className="mb-6">
                    <ResultVisualization
                      resultType={survey?.result_type || 'flat'}
                      groupResults={resp.group_results}
                      overallPercentage={resp.overall_percentage}
                      score={resp.score}
                      scoreBreakdown={resp.score_breakdown}
                    />
                  </div>
                )}

                {/* Q&A detail */}
                <dl className="space-y-3">
                  {Object.entries(resp.responses).map(([questionId, answer]) => (
                    <div key={questionId} className="flex justify-between py-2 border-b border-border last:border-0">
                      <dt className="text-sm text-foreground-muted">{getQuestionText(questionId)}</dt>
                      <dd className="text-sm font-medium text-foreground ml-4">
                        {formatAnswer(answer)}
                        {resp.score_breakdown && resp.score_breakdown[questionId] != null && (
                          <span className="ml-2 text-xs text-foreground-muted">
                            <FormattedMessage id="responses.points" values={{ points: resp.score_breakdown[questionId] }} />
                          </span>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  )
}

export default SurveyResponsesPage
